import React from 'react'
import { Link } from 'react-router-dom'
import { ZoomResourceLayout } from '../components/zoom'

const SUPPORT_EMAIL = 'service@abroad.io'
const SUPPORT_EMAIL_URL =
  'mailto:service@abroad.io?subject=Abroad%20Zoom%20integration%20support'
const ZOOM_SUPPORT_URL = 'https://support.zoom.com'
const PRIVACY_POLICY_URL = 'https://www.abroad.io/privacy-policy'
const TERMS_URL = 'https://www.abroad.io/terms-and-conditions'

/**
 * Public Zoom Marketplace support page.
 * Covers hours, first-response SLA, and support channels required by Zoom.
 */
const ZoomSupport = () => {
  return (
    <ZoomResourceLayout
      title='Zoom Integration Support'
      description="Contact Abroad support for help with Abroad's Zoom integration."
      path='/zoom/support'
      eyebrow='Abroad support'
      heading="Help with Abroad's Zoom integration"
      intro='The Abroad team supports connecting your Zoom account, Zoom meetings for sessions booked in Abroad, AI meeting summaries, and removing the integration.'>
      <div className='zoom-resource-sections'>
        <section
          className='bg-white border-radius-custom p-32px mb-64px prosperity-card hover-lift'
          aria-label='Support commitments'>
          <div className='text-gold-light text-uppercase font-montserrat small-title-2 fw-bold tracking-widest mb-3'>
            Support availability
          </div>
          <h2 className='font-heading sub-title text-rich-navy mb-4'>
            Direct help from the Abroad team
          </h2>

          <div className='row g-4'>
            <div className='col-md-4'>
              <div className='zoom-resource-stat h-100'>
                <p className='font-heading zoom-resource-stat__value text-rich-navy mb-2'>
                  24
                </p>
                <p className='text-gold-light text-uppercase font-montserrat small-title fw-bold tracking-widest mb-2'>
                  Hours
                </p>
                <p className='font-paragraph small-paragraph text-slate-gray mb-0'>
                  Maximum first-response time during operating days.
                </p>
              </div>
            </div>
            <div className='col-md-8'>
              <dl className='zoom-resource-facts mb-0'>
                <div className='mb-4'>
                  <dt className='text-gold-light text-uppercase font-montserrat small-title fw-bold tracking-widest mb-2'>
                    Hours of operation
                  </dt>
                  <dd className='font-paragraph small-paragraph text-slate-gray mb-0'>
                    Monday–Friday, 9:00 AM–5:00 PM Eastern Time, excluding US
                    holidays
                  </dd>
                </div>
                <div className='mb-4'>
                  <dt className='text-gold-light text-uppercase font-montserrat small-title fw-bold tracking-widest mb-2'>
                    First Response SLA
                  </dt>
                  <dd className='font-paragraph small-paragraph text-slate-gray mb-0'>
                    Within 24 hours during operating days. Messages received
                    outside support hours are handled on the next operating day.
                  </dd>
                </div>
                <div>
                  <dt className='text-gold-light text-uppercase font-montserrat small-title fw-bold tracking-widest mb-2'>
                    Support channel
                  </dt>
                  <dd className='font-paragraph small-paragraph text-slate-gray mb-0'>
                    Email
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </section>

        <section
          className='bg-white border-radius-custom p-32px mb-64px prosperity-card hover-lift'
          aria-labelledby='contact-support'>
          <div className='text-gold-light text-uppercase font-montserrat small-title-2 fw-bold tracking-widest mb-3'>
            Contact support
          </div>
          <h2
            id='contact-support'
            className='font-heading sub-title text-rich-navy mb-3'>
            Create a support case
          </h2>
          <p className='font-paragraph small-paragraph text-slate-gray mb-4'>
            Email the Abroad support team. Each email opens a support case, and
            we reply to the address you send from.
          </p>
          <a
            className='btn btn-gold text-white text-uppercase prosperity-button-cta font-paragraph small-paragraph-bold'
            href={SUPPORT_EMAIL_URL}>
            Create a support case by email
          </a>
          <p className='font-paragraph small-paragraph-3 text-slate-gray mt-3 mb-0'>
            Email support:{' '}
            <a className='text-gold-light' href={SUPPORT_EMAIL_URL}>
              {SUPPORT_EMAIL}
            </a>
          </p>
        </section>

        <section
          className='bg-white border-radius-custom p-32px mb-64px prosperity-card hover-lift'
          aria-labelledby='help-topics'>
          <div className='text-gold-light text-uppercase font-montserrat small-title-2 fw-bold tracking-widest mb-3'>
            Support scope
          </div>
          <h2
            id='help-topics'
            className='font-heading sub-title text-rich-navy mb-4'>
            What we can help with
          </h2>
          <ul className='zoom-resource-check-list list-unstyled mb-4'>
            <li className='font-paragraph small-paragraph text-slate-gray'>
              Connecting, reconnecting, or disconnecting your Zoom account in
              Abroad.
            </li>
            <li className='font-paragraph small-paragraph text-slate-gray'>
              Sessions that received a non-Zoom link or no link.
            </li>
            <li className='font-paragraph small-paragraph text-slate-gray'>
              Zoom meetings that were not deleted after cancellation.
            </li>
            <li className='font-paragraph small-paragraph text-slate-gray'>
              AI meeting summaries missing from Session Insights.
            </li>
            <li className='font-paragraph small-paragraph text-slate-gray'>
              Requests to access or delete data Abroad stores about your Zoom
              connection.
            </li>
          </ul>
          <div className='zoom-resource-note font-paragraph small-paragraph text-slate-gray'>
            <strong className='text-rich-navy'>Contact Zoom Support</strong> at{' '}
            <a className='text-gold-light' href={ZOOM_SUPPORT_URL}>
              support.zoom.com
            </a>{' '}
            for Zoom sign-in problems, Zoom plan or billing questions, AI
            Companion availability, or audio and video issues during a meeting.
          </div>
        </section>

        <section
          className='bg-white border-radius-custom p-32px mb-64px prosperity-card hover-lift'
          aria-labelledby='help-us-help'>
          <div className='text-gold-light text-uppercase font-montserrat small-title-2 fw-bold tracking-widest mb-3'>
            Before you send
          </div>
          <h2
            id='help-us-help'
            className='font-heading sub-title text-rich-navy mb-3'>
            Help us resolve it faster
          </h2>
          <p className='font-paragraph small-paragraph text-slate-gray mb-4'>
            Include the following details where they apply:
          </p>
          <ul className='zoom-resource-check-list list-unstyled mb-4'>
            <li className='font-paragraph small-paragraph text-slate-gray'>
              The email address of your Abroad account.
            </li>
            <li className='font-paragraph small-paragraph text-slate-gray'>
              The email address of the connected Zoom account.
            </li>
            <li className='font-paragraph small-paragraph text-slate-gray'>
              What you were trying to do and the exact message shown.
            </li>
            <li className='font-paragraph small-paragraph text-slate-gray'>
              The session date, time (with time zone), and client name.
            </li>
            <li className='font-paragraph small-paragraph text-slate-gray'>
              For summary issues: whether AI Companion meeting summary was on,
              and when the meeting started and ended.
            </li>
            <li className='font-paragraph small-paragraph text-slate-gray'>
              A screenshot with personal and sensitive information removed.
            </li>
          </ul>
          <div className='zoom-resource-note font-paragraph small-paragraph text-slate-gray'>
            <strong className='text-rich-navy'>
              Keep your account secure.
            </strong>{' '}
            Abroad support will never ask for your Zoom or Abroad password.
          </div>
        </section>

        <section
          className='bg-white border-radius-custom p-32px mb-64px prosperity-card hover-lift text-center'
          aria-labelledby='self-service'>
          <div className='text-gold-light text-uppercase font-montserrat small-title-2 fw-bold tracking-widest mb-3'>
            Resources
          </div>
          <h2
            id='self-service'
            className='font-heading sub-title text-rich-navy mb-3'>
            Looking for setup instructions?
          </h2>
          <p className='font-paragraph small-paragraph text-slate-gray mb-4 mx-auto zoom-resource-closing-copy'>
            The integration guide covers adding, using, troubleshooting, and
            removing Abroad's Zoom integration. See also the{' '}
            <a className='text-gold-light' href={PRIVACY_POLICY_URL}>
              Privacy Policy
            </a>{' '}
            and{' '}
            <a className='text-gold-light' href={TERMS_URL}>
              Terms and Conditions
            </a>
            .
          </p>
          <Link
            className='btn btn-gold text-white text-uppercase prosperity-button-cta font-paragraph small-paragraph-bold'
            to='/zoom/documentation'>
            Read the integration guide
          </Link>
        </section>
      </div>
    </ZoomResourceLayout>
  )
}

export default ZoomSupport
